import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, User, ArrowLeft, Facebook, Twitter, Linkedin, Link as LinkIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { SEOHead } from "@/components/SEOHead";
import BlogSidebar from "@/components/BlogSidebar";

export default function BlogPostDecompressionSciatica() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background font-sans text-foreground flex flex-col">
      <SEOHead 
        title="Sciatica Relief: How Spinal Decompression Targets the Root Cause | Longview, TX"
        description="Struggling with shooting leg pain and sciatica? Learn how DRX9000 Spinal Decompression treats the root cause without surgery in East Texas."
        ogImage="https://vibe.filesafe.space/1774569058151009111/assets/34920f7b-9e8d-4a9f-aacf-4ee0318ec5c7.png"
        canonical="https://woodsspine.com/blog/decompression-sciatica"
      />
      <Navbar />
      <main className="flex-grow container mx-auto px-4 py-12">
        <Button variant="ghost" className="mb-6 hover:bg-transparent pl-0 text-muted-foreground hover:text-foreground" asChild>
          <Link to="/blog">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blog
          </Link>
        </Button>
        <div className="lg:grid lg:grid-cols-[1fr_350px] gap-12">
          <div className="space-y-8">
            <header className="space-y-4">
              <Badge className="bg-accent text-white hover:bg-accent/90 border-0 shadow-sm">
                Spinal Decompression
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold leading-tight text-primary">
                Sciatica Relief: How Spinal Decompression Targets the Root Cause
              </h1>
              <div className="flex flex-wrap items-center text-sm text-muted-foreground gap-4">
                <Link to="/about#dr-tucker-woods" className="flex items-center hover:text-accent transition-colors">
                  <User className="w-4 h-4 mr-2" />
                  Dr. Tucker Woods
                </Link>
                <span className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  May 02, 2026
                </span>
                <span>• 5 min read</span>
              </div>
            </header>

            <img 
              src="https://wsrv.nl/?url=https://vibe.filesafe.space/1774569058151009111/assets/34920f7b-9e8d-4a9f-aacf-4ee0318ec5c7.png&w=1280&output=webp&q=80" 
              alt="Patient experiencing sciatica and leg pain exploring spinal decompression in Longview TX" 
              className="w-full rounded-2xl aspect-video object-cover"
              fetchPriority="high"
              decoding="async"
            />

            <article className="prose prose-lg dark:prose-invert max-w-none">
              <p className="text-xl font-medium text-foreground">
                If you have ever felt a sharp, electric shock radiating from your lower back down through your buttock and into your leg, you know the agonizing reality of sciatica. It makes sitting, standing, and sleeping incredibly difficult. Discover how advanced spinal decompression is helping patients find lasting relief by targeting the root cause.
              </p>
              <p>
                Sciatica is not actually a condition itself; rather, it is a symptom of an underlying problem in your lower back. The sciatic nerve is the longest and thickest nerve in the human body. When it becomes pinched or irritated—usually by a herniated disc, bone spur, or spinal stenosis—it sends shooting pain, numbness, and weakness down the leg. Masking this pain with medication does nothing to remove the pressure on the nerve.
              </p>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">Why Traditional Treatments Often Fail Sciatica</h2>
              <p>
                Many patients with sciatica are prescribed muscle relaxers, pain pills, or steroid injections. While these can temporarily dull the pain and reduce localized inflammation, they fail to address the mechanical compression of the sciatic nerve. If a bulging spinal disc is physically pressing against the nerve root, chemical solutions (medications) will only provide temporary relief. Physical therapy can help strengthen the core, but if the disc is severely herniated, exercise alone may not be enough to retract the disc material.
              </p>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">How Spinal Decompression Relieves Nerve Pressure</h2>
              <p>
                <Link to="/longview-spinal-decompression" className="text-accent hover:underline">DRX9000 Spinal Decompression</Link> offers a highly effective, non-surgical solution for sciatic nerve pain. By utilizing advanced computer technology, the decompression table applies a gentle, precise pulling force to the lower spine. 
              </p>
              <p>
                This targeted stretching creates a negative pressure (a vacuum effect) within the spinal discs. This vacuum helps to draw the bulging or herniated disc material back into the center of the disc, physically pulling it away from the irritated sciatic nerve. As the pressure is removed from the nerve, the shooting leg pain, tingling, and numbness begin to subside. Additionally, this process pulls oxygen, water, and nutrient-rich fluids into the disc, promoting long-term healing.
              </p>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">Clinical Evidence and Credibility</h2>
              <p>
                Non-surgical spinal decompression is FDA-cleared and widely supported by clinical research for the treatment of radiculopathy (nerve pain like sciatica) caused by herniated discs. Peer-reviewed studies have demonstrated that computerized decompression significantly lowers intradiscal pressure, which is the exact mechanical change required to retract disc herniations and relieve sciatic nerve compression. Many patients who were told surgery was their only option have successfully avoided the operating room through this therapy.
              </p>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">Local Sciatica Relief in Longview, TX</h2>
              <p>
                At Woods Spine & Wellness in Longview, TX, Dr. Tucker Woods and Dr. Lance Potter specialize in treating severe, chronic sciatica. We understand how this condition can rob you of your mobility and joy. By bringing the state-of-the-art DRX9000 system to East Texas, we provide our community with a powerful alternative to spinal surgery right here in Gregg County. To learn more about the specific conditions we manage, visit our <Link to="/what-we-treat" className="text-accent hover:underline">What We Treat</Link> page.
              </p>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">Frequently Asked Questions</h2>
              
              <div className="space-y-6 mt-6">
                <div className="border-b border-border pb-4">
                  <h3 className="text-lg font-bold text-primary mb-2">How quickly does spinal decompression relieve sciatica?</h3>
                  <p className="text-muted-foreground">Many patients experience a noticeable reduction in their radiating leg pain within the first few sessions. However, to ensure the disc fully heals and the relief is long-lasting, completing the full protocol of 20-24 sessions is critical.</p>
                </div>
                <div className="border-b border-border pb-4">
                  <h3 className="text-lg font-bold text-primary mb-2">Is the decompression treatment uncomfortable?</h3>
                  <p className="text-muted-foreground">Not at all. The DRX9000 uses advanced sensors to monitor your muscle tension, ensuring the pull is gentle and smooth. Most patients find the treatment very relaxing, and many even fall asleep during their session.</p>
                </div>
                <div className="pb-2">
                  <h3 className="text-lg font-bold text-primary mb-2">Can I do this if I've had sciatica for years?</h3>
                  <p className="text-muted-foreground">Yes. Spinal decompression is highly effective for both acute and chronic sciatica. Even if you have been suffering for years, relieving the mechanical pressure on the nerve can still facilitate healing and significant pain reduction.</p>
                </div>
              </div>

            </article>

            {/* Social Sharing */}
            <div className="flex items-center gap-4 pt-8 border-t border-border">
              <span className="font-medium text-muted-foreground">Share this article:</span>
              <div className="flex gap-2">
                <Button variant="outline" size="icon" className="rounded-full hover:text-accent">
                  <Facebook className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="rounded-full hover:text-accent">
                  <Twitter className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="rounded-full hover:text-accent">
                  <Linkedin className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="rounded-full hover:text-accent">
                  <LinkIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* CTA Block */}
            <div className="bg-secondary/30 p-8 rounded-xl border border-border mt-8">
              <h3 className="text-2xl font-bold text-primary mb-3">Stop living with shooting leg pain</h3>
              <p className="text-muted-foreground mb-6">
                If sciatica is keeping you from doing the things you love, schedule a consultation to see if DRX9000 Spinal Decompression can help you heal.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button className="bg-accent text-white hover:bg-accent/90" asChild>
                  <Link to="/appointment-scheduling">Get Started</Link>
                </Button>
                <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground" asChild>
                  <Link to="/contact">Contact Us</Link>
                </Button>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="text-sm text-muted-foreground border-t border-border pt-6 mt-8">
              Disclaimer: The information provided in this article is for educational purposes only and does not constitute medical advice. Please consult with a healthcare provider before starting any new treatment.
            </div>
          </div>

          <BlogSidebar />
        </div>
      </main>
      <Footer />
    </div>
  );
}
